import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface ItemStyle {
  width: string;
  height: string;
  borderRadius: string;
  backgroundColor: string;
  color: string;
  shape: "rectangle" | "circle" | "rounded" | "diamond" | "triangle";
}

interface DraggedItem {
  id: string;
  style: ItemStyle;
}

interface DraggedItemsContextType {
  storedItems: DraggedItem[];
  addItem: (id: string) => void;
  removeItem: (id: string) => void;
  updateItemStyle: (id: string, style: Partial<ItemStyle>) => void;
}

const STORAGE_KEY = "admin_dashboard_dragged_items";

const DraggedItemsContext = createContext<DraggedItemsContextType | undefined>(
  undefined
);

export const useDraggedItems = () => {
  const context = useContext(DraggedItemsContext);
  if (!context) {
    throw new Error(
      "useDraggedItems must be used within a DraggedItemsProvider"
    );
  }
  return context;
};

interface DraggedItemsProviderProps {
  children: ReactNode;
}

const getShapeStyle = (shape: string) => {
  switch (shape) {
    case "circle":
      return {
        borderRadius: "50%",
        clipPath: "none",
      };
    case "rounded":
      return {
        borderRadius: "4px",
        clipPath: "none",
      };
    case "diamond":
      return {
        borderRadius: "0",
        clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
      };
    case "triangle":
      return {
        borderRadius: "0",
        clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
      };
    default: // rectangle
      return {
        borderRadius: "0",
        clipPath: "none",
      };
  }
};

export const DraggedItemsProvider: React.FC<DraggedItemsProviderProps> = ({
  children,
}) => {
  const [storedItems, setStoredItems] = useState<DraggedItem[]>(() => {
    const savedItems = localStorage.getItem(STORAGE_KEY);
    return savedItems ? JSON.parse(savedItems) : [];
  });

  const addItem = (id: string) => {
    if (!storedItems.some((item) => item.id === id)) {
      const newItem: DraggedItem = {
        id,
        style: {
          width: "auto",
          height: "auto",
          borderRadius: "10px",
          backgroundColor: "white",
          color: "#000000",
          shape: "rectangle",
        },
      };
      const newItems = [...storedItems, newItem];
      setStoredItems(newItems);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newItems));
    }
  };

  const removeItem = (id: string) => {
    const newItems = storedItems.filter((item) => item.id !== id);
    setStoredItems(newItems);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newItems));
  };

  const updateItemStyle = (itemId: string, newStyle: Partial<ItemStyle>) => {
    setStoredItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId
          ? {
              ...item,
              style: {
                ...item.style,
                ...newStyle,
                ...(newStyle.shape && newStyle.shape !== item.style.shape
                  ? getShapeStyle(newStyle.shape)
                  : {}),
              },
            }
          : item
      )
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedItems));
  };

  return (
    <DraggedItemsContext.Provider
      value={{ storedItems, addItem, removeItem, updateItemStyle }}
    >
      {children}
    </DraggedItemsContext.Provider>
  );
};
